import {
  Button,
  Circle,
  HStack,
  IconButton,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useOverlay } from "modules/providers/overlay-provider";
import { useUsers } from "modules/providers/users-provider";
import React, { memo, useCallback, useMemo } from "react";
import colors from "app/colors";
import { TASK_DRAFT_ID, TextAlignment } from "modules/utils/Constants";
import useMessageThreadDraft from "modules/hooks/use-message-thread-draft";
import { DRAWER_TYPE } from "modules/providers/overlay-provider/overlay-provider";
import {
  MessageThreadType,
  TaskPriorityType,
  TaskStatusType,
  UserRoleType,
} from "gql/graphql";
import { CheckPlusIcon } from "modules/icons";

interface IProps {
  userId: string;
  displayText?: boolean;
  textAlignment?: TextAlignment;
}

const NewTaskButton = ({
  userId,
  displayText = true,
  textAlignment = "inline",
}: IProps) => {
  const { openDrawer } = useOverlay();
  const { createDraft } = useMessageThreadDraft({
    draftId: TASK_DRAFT_ID,
  });
  const { usersById } = useUsers();

  const user = useMemo(() => usersById[userId], [usersById, userId]);

  const onNewTaskClicked = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      e.preventDefault();
      await createDraft(
        {
          type: MessageThreadType.Task,
          draftType: "new-thread",
          task: {
            assignedUserId: userId,
            priorityType: TaskPriorityType.None,
            statusType: TaskStatusType.NotStarted,
          },
        },
        true,
      );
      openDrawer({
        type: DRAWER_TYPE.COMPOSE_TASK,
      });
    },
    [createDraft, openDrawer, userId],
  );

  if (!user || !user.active || user.roleType === UserRoleType.Email) {
    return null;
  }

  if (!displayText) {
    return (
      <IconButton
        onClick={onNewTaskClicked}
        aria-label="New Task"
        variant="hover"
        bg={colors.greenPrimary}
        rounded="full"
        size="sm"
        icon={<CheckPlusIcon boxSize={6} fill={colors.white} />}
      />
    );
  }

  if (textAlignment === "inline") {
    return (
      <Button
        onClick={onNewTaskClicked}
        aria-label="New Task"
        variant="ghost"
        height="auto"
        px={0}
      >
        <HStack spacing={1}>
          <Circle bg={colors.greenPrimary} size={6}>
            <CheckPlusIcon boxSize={4} fill={colors.white} />
          </Circle>
          {displayText && (
            <Text
              fontSize={10}
              fontWeight={500}
              color={colors.gray50}
              textAlign="center"
            >
              New Task
            </Text>
          )}
        </HStack>
      </Button>
    );
  }

  return (
    <Button
      onClick={onNewTaskClicked}
      aria-label="New Task"
      variant="hover"
      height="auto"
      px={0}
    >
      <VStack spacing={1}>
        <Circle bg={colors.greenPrimary} size={6}>
          <CheckPlusIcon boxSize={4} fill={colors.white} />
        </Circle>
        <Text
          fontSize={10}
          fontWeight={500}
          color={colors.gray50}
          textAlign="center"
        >
          Assign
          <br />
          Task
        </Text>
      </VStack>
    </Button>
  );
};

export default memo(NewTaskButton);
